import { useEffect, useState } from "react";
import { INDIAN_STATES } from "../lib/indianStates";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useEditAccessRequest } from "./EditAccessRequest";
import { toast } from "sonner";
import { Plus, X, Building2 } from "lucide-react";
import { Badge } from "./ui/badge";

const CLIENT_TYPES = ["ATL 1.0", "ATL 2.0", "Private School", "PM-SHRI JNV", "PM-SHRI KV", "College", "CSR", "Other", "Non ATL"];
const CONTACT_TYPES = ["Chairman", "Principal", "School Teacher", "ATL In-Charge", "Other"];
const YES_NO = ["Yes", "No"];

export function emptyClientForm() {
  return {
    client_name: "", u_dise: "", atl_code: "", client_type: "", client_types: [], client_type_other: "", client_email: "",
    important_remark: "",
    address: "", pin_code: "", district: "", state: "",
    contacts: [newContact()],
    has_whatsapp_group: false, whatsapp_group_name: "", whatsapp_no_reason: "",
    atl_2nd_tranche: "", atl_3rd_tranche: "", atl_has_drive: "", atl_drive_link: "", atl_school_funds: "",
    kit_required: false, mou_required: false, mou_signed: false,
    assigned_to_id: "", assigned_to_name: "",
    documentation_required: false, documentation_assignee_ids: [], documentation_assignee_names: [],
    is_hot: false, hot_reason: "",
  };
}

function newContact() {
  const _uid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return { _uid, name: "", email: "", phone: "", type: "", type_other: "" };
}

export default function ClientFormDialog({ open, onOpenChange, initial, onSaved, title = "New Client", submitLabel = "Submit" }) {
  const [form, setForm] = useState(emptyClientForm());
  const [people, setPeople] = useState([]);
  const [udiseConflict, setUdiseConflict] = useState(null);
  const [reassignReason, setReassignReason] = useState("");
  // v109.9.7 — Save-and-request wiring for extended module (crm_clients).
  const { dialog: editReqDialog, wrapMutation: wrapEditRequest } = useEditAccessRequest();

  useEffect(() => { api.get("/directory").then(({ data }) => setPeople(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (open) {
      const initContacts = initial?.contacts?.length
        ? initial.contacts.map((c) => (c._uid ? c : { ...c, _uid: newContact()._uid }))
        : [newContact()];
      setForm({ ...emptyClientForm(), ...(initial || {}), contacts: initContacts });
      setUdiseConflict(null); setReassignReason("");
    }
  }, [open, initial]);

  const checkUdise = async (u) => {
    if (!u || initial?.id) return setUdiseConflict(null);
    try {
      const { data } = await api.get(`/crm/udise-check/${encodeURIComponent(u)}`);
      setUdiseConflict(data.exists ? data : null);
    } catch (e) {
      console.error("ClientFormDialog: U-DISE lookup failed", e);
    }
  };

  const addContact = () => setForm((f) => ({ ...f, contacts: [...f.contacts, newContact()] }));
  const removeContact = (i) => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));
  const updateContact = (i, key, val) => setForm((f) => ({ ...f, contacts: f.contacts.map((c, idx) => idx === i ? { ...c, [key]: val } : c) }));

  // Backfill client_types from legacy client_type on open
  useEffect(() => {
    if (!open) return;
    setForm((f) => {
      if ((f.client_types || []).length === 0 && f.client_type) {
        const parts = String(f.client_type).split(",").map((s) => s.trim()).filter(Boolean);
        return { ...f, client_types: parts };
      }
      return f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleClientType = (t) => {
    setForm((f) => {
      const set = new Set(f.client_types || []);
      if (set.has(t)) set.delete(t); else set.add(t);
      const arr = Array.from(set);
      return { ...f, client_types: arr, client_type: arr.join(", ") };
    });
  };

  const submit = async () => {
    const typesCount = (form.client_types || []).length;
    if (!form.client_name.trim() || !form.u_dise.trim() || typesCount === 0) {
      toast.error("Client Name, U-Dise and at least one Client Type are required"); return;
    }
    if ((form.client_types || []).includes("Other") && !(form.client_type_other || "").trim()) {
      toast.error("Please specify the 'Other' client type"); return;
    }
    // Documentation Support is only meaningful for ATL 1.0 / ATL 2.0 schools.
    if (form.documentation_required) {
      const hasATL = (form.client_types || []).includes("ATL 1.0") || (form.client_types || []).includes("ATL 2.0");
      if (!hasATL) {
        toast.error("Documentation Support is available only for ATL 1.0 or ATL 2.0 clients");
        return;
      }
    }
    // Validate Contact Other type label
    const badContact = form.contacts.find((c) => c.type === "Other" && !(c.type_other || "").trim());
    if (badContact) { toast.error("Please specify the 'Other' contact type for every contact"); return; }
    try {
      // Strip frontend-only _uid before sending to backend
      const payload = {
        ...form,
        client_type: (form.client_types || []).join(", "),  // legacy single field stays as comma-joined
        contacts: form.contacts.map((c) => {
          const { _uid, ...rest } = c;
          return rest;
        }),
      };
      if (initial?.id) {
        const result = await wrapEditRequest(
          () => api.patch(`/records/crm_clients/${initial.id}`, payload),
          {
            moduleKey: "crm_clients",
            recordId: initial.id,
            recordLabel: form.client_name || form.client_code || initial.id,
            proposedPayload: payload,
            stageOn403: true,
          },
        );
        if (!result) { onOpenChange(false); return; }
      } else await api.post("/records/crm_clients", payload);
      toast.success(initial?.id ? "Client updated" : "Client submitted");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const submitReassign = async () => {
    try {
      await api.post("/crm/reassign-requests", {
        client_id: udiseConflict.client_id, new_assignee_id: form.assigned_to_id || "self",
        new_assignee_name: "Self", reason: reassignReason,
      });
      toast.success("Reassignment request sent");
      setUdiseConflict(null); setReassignReason(""); onOpenChange(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const isATL10 = (form.client_types || []).includes("ATL 1.0");
  const isATL20 = (form.client_types || []).includes("ATL 2.0");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <div className="relative bg-gradient-to-br overflow-hidden rounded-t-lg from-sky-500 via-cyan-600 to-blue-700 px-6 py-5 text-white">
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center">
              <Building2 size={22} />
            </div>
            <div>
              <DialogTitle className="heading text-lg font-extrabold">{title}</DialogTitle>
              <div className="text-[11.5px] text-white/85 mt-0.5">School / Client master record. Fields adapt by Client Type.</div>
            </div>
          </div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>Form</DialogTitle></DialogHeader>
        {/* v109.9.168 — Creator + created_at breadcrumb at the top of the client detail. */}
        {initial?.id && (
          <div className="px-6 pt-4 -mb-2 text-[11px] text-slate-500 dark:text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="cfd-created-meta">
            <span>Created by <b className="text-slate-700 dark:text-slate-200">{initial?.created_by_name || "—"}</b></span>
            {initial?.created_at && (
              <span>· {new Date(initial.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            )}
            {initial?.updated_at && initial.updated_at !== initial.created_at && (
              <span className="text-slate-400">· last edited {new Date(initial.updated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-6 py-4">
          {udiseConflict && (
            <div className="md:col-span-2 border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4" data-testid="cfd-conflict">
              <div className="text-[12px] font-bold text-amber-800 uppercase tracking-wider">U-Dise already exists</div>
              <div className="text-sm text-slate-800 mt-1">
                &quot;<strong>{udiseConflict.client_name}</strong>&quot; is already in the database, assigned to <strong>{udiseConflict.assigned_to_name || "—"}</strong>.
              </div>
              <Textarea value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} rows={2} placeholder="Reason for reassignment request…" className="mt-2" data-testid="cfd-reassign-reason" />
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setUdiseConflict(null)}>Dismiss</Button>
                <Button size="sm" onClick={submitReassign} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="cfd-reassign-submit">Request Reassignment</Button>
              </div>
            </div>
          )}
          {/* Important Remark — surfaced at the top of every client-detail
              view across modules so it's the first thing anyone reads. */}
          <div className="md:col-span-2 border-2 border-amber-300 bg-amber-50/70 dark:bg-amber-900/20 rounded-md p-3" data-testid="cfd-important-remark-wrap">
            <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
              ⚠ Important Remark
              <span className="text-[10px] normal-case font-normal text-slate-500">— visible to everyone who can see this client</span>
            </Label>
            <Textarea
              rows={2}
              value={form.important_remark || ""}
              onChange={(e) => setForm({ ...form, important_remark: e.target.value })}
              placeholder="Any critical note about this school (e.g. payment terms, special clearance, sensitive contact) — shown prominently in every module."
              className="mt-1.5 bg-white"
              data-testid="cfd-important-remark"
            />
          </div>
          <F label="Client / School Name *"><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="cfd-name" /></F>
          <F label="U-Dise (Unique Key) *"><Input value={form.u_dise} onChange={(e) => setForm({ ...form, u_dise: e.target.value })} onBlur={(e) => checkUdise(e.target.value)} data-testid="cfd-udise" /></F>
          {isATL10 && (
            <F label="ATL Code (required for ATL 1.0)">
              <Input value={form.atl_code} onChange={(e) => setForm({ ...form, atl_code: e.target.value })} placeholder="Eg. ATL-CODE-12345" data-testid="cfd-atl-code" />
            </F>
          )}
          <F label="Client Type * (pick one or more)">
            <div className="flex flex-wrap gap-1.5" data-testid="cfd-type-multi">
              {CLIENT_TYPES.map((t) => {
                const active = (form.client_types || []).includes(t);
                return (
                  <button
                    key={t} type="button"
                    onClick={() => toggleClientType(t)}
                    className={`px-3 py-1.5 text-[12px] rounded-full border-2 transition-all ${active ? "bg-gradient-to-br from-sky-500 to-cyan-600 border-sky-500 text-white font-bold shadow-sm" : "bg-white border-sky-200 text-sky-700 hover:border-sky-400"}`}
                    data-testid={`cfd-type-${t.replace(/[^A-Za-z0-9]/g, "_")}`}
                  >
                    {active && <span className="mr-1">✓</span>}{t}
                  </button>
                );
              })}
            </div>
            {(form.client_types || []).length > 0 && (
              <div className="text-[10px] text-slate-500 mt-1.5">
                Selected: <span className="font-semibold text-slate-700">{form.client_types.join(", ")}</span>
              </div>
            )}
            {(form.client_types || []).includes("Other") && (
              <div className="mt-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Specify "Other" Type *</Label>
                <Input
                  value={form.client_type_other || ""}
                  onChange={(e) => setForm({ ...form, client_type_other: e.target.value })}
                  placeholder="Eg. Govt. Aided School, Coaching Centre, NGO…"
                  className="mt-1.5"
                  data-testid="cfd-client-type-other"
                />
              </div>
            )}
          </F>
          <F label="Client Email"><Input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="cfd-email" /></F>
          <F label="Complete Address" full><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cfd-address" /></F>
          <F label="Pin Code"><Input value={form.pin_code} onChange={(e) => setForm({ ...form, pin_code: e.target.value })} data-testid="cfd-pin" /></F>
          <F label="District"><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="cfd-district" /></F>
          <F label="State">
            <select
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              className="h-10 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-3 w-full"
              data-testid="cfd-state"
            >
              <option value="">— Select State / UT —</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </F>

          <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="pk-table-header">Contacts ({form.contacts.length})</div>
              <Button type="button" size="sm" variant="outline" onClick={addContact} className="h-8 text-xs" data-testid="cfd-add-contact"><Plus size={12} className="mr-1" /> Add</Button>
            </div>
            <div className="space-y-3">
              {form.contacts.map((c, i) => (
                <div key={c._uid || i} className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Contact #{i + 1}</div>
                    {form.contacts.length > 1 && <button type="button" onClick={() => removeContact(i)} className="text-red-500"><X size={14} /></button>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <F label="Name"><Input value={c.name} onChange={(e) => updateContact(i, "name", e.target.value)} data-testid={`cfd-c-name-${i}`} /></F>
                    <F label="Email"><Input value={c.email} onChange={(e) => updateContact(i, "email", e.target.value)} data-testid={`cfd-c-email-${i}`} /></F>
                    <F label="Number"><Input value={c.phone} onChange={(e) => updateContact(i, "phone", e.target.value)} data-testid={`cfd-c-phone-${i}`} /></F>
                    <F label="Type">
                      <Select value={c.type} onValueChange={(v) => updateContact(i, "type", v)}>
                        <SelectTrigger data-testid={`cfd-c-type-${i}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>{CONTACT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </F>
                    {c.type === "Other" && (
                      <F label="Specify Contact Type *" full>
                        <Input
                          value={c.type_other || ""}
                          onChange={(e) => updateContact(i, "type_other", e.target.value)}
                          placeholder="Eg. ATL Coordinator, Vendor Partner…"
                          data-testid={`cfd-c-type-other-${i}`}
                        />
                      </F>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
            <div className="flex items-center gap-3 mb-2">
              <Switch checked={form.has_whatsapp_group} onCheckedChange={(v) => setForm({ ...form, has_whatsapp_group: v })} data-testid="cfd-has-wa" />
              <span className="text-sm font-semibold text-slate-800">Do we have a WhatsApp Group with this client?</span>
            </div>
            {form.has_whatsapp_group
              ? <F label="WhatsApp Group Name" full><Input value={form.whatsapp_group_name} onChange={(e) => setForm({ ...form, whatsapp_group_name: e.target.value })} data-testid="cfd-wa-name" /></F>
              : <F label="If No, please explain why" full><Textarea rows={2} value={form.whatsapp_no_reason} onChange={(e) => setForm({ ...form, whatsapp_no_reason: e.target.value })} data-testid="cfd-wa-reason" /></F>}
          </div>

          {isATL20 && (
            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <div className="pk-table-header mb-3 text-[var(--pk-accent)]">ATL 2.0 — Kit & MOU</div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 px-3 py-2 bg-amber-50/60 border border-amber-200 rounded-md">
                  <Switch checked={!!form.kit_required} onCheckedChange={(v) => setForm({ ...form, kit_required: v })} data-testid="cfd-kit-required" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Do we need to send the Kit?</div>
                    <div className="text-[11px] text-slate-600">Turning this ON will auto-generate an Order (status: Pending Invoice) for this school once you save the client.</div>
                  </div>
                </div>
                {initial?.kit_order_no && (
                  <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5" data-testid="cfd-kit-order-link">
                    Linked Order: <span className="mono font-semibold">{initial.kit_order_no}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 px-3 py-2 bg-blue-50/40 border border-blue-200 rounded-md">
                  <Switch checked={!!form.mou_required} onCheckedChange={(v) => setForm({ ...form, mou_required: v, mou_signed: v ? form.mou_signed : false })} data-testid="cfd-mou-required" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Is an MOU required?</div>
                    <div className="text-[11px] text-slate-600">When ON, please indicate whether it&apos;s been signed.</div>
                  </div>
                </div>
                {form.mou_required && (
                  <div className="flex items-center gap-3 pl-6">
                    <Switch checked={!!form.mou_signed} onCheckedChange={(v) => setForm({ ...form, mou_signed: v })} data-testid="cfd-mou-signed" />
                    <span className="text-sm text-slate-800">MOU Signed</span>
                    <Badge className={form.mou_signed ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]" : "bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]"}>{form.mou_signed ? "Signed" : "Pending"}</Badge>
                  </div>
                )}
              </div>
            </div>
          )}

          {isATL10 && (
            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <div className="pk-table-header mb-3 text-[var(--pk-accent)]">ATL 1.0 — Tranche & Documentation</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label="Applied for 2nd Tranche?">
                  <Select value={form.atl_2nd_tranche} onValueChange={(v) => setForm({ ...form, atl_2nd_tranche: v })}>
                    <SelectTrigger data-testid="cfd-atl-2"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>{YES_NO.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Applied for 3rd Tranche?">
                  <Select value={form.atl_3rd_tranche} onValueChange={(v) => setForm({ ...form, atl_3rd_tranche: v })}>
                    <SelectTrigger data-testid="cfd-atl-3"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>{YES_NO.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Documents Folder on Drive?">
                  <Select value={form.atl_has_drive} onValueChange={(v) => setForm({ ...form, atl_has_drive: v })}>
                    <SelectTrigger data-testid="cfd-atl-drive"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>{YES_NO.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                {form.atl_has_drive === "Yes" && <F label="Drive Link"><Input value={form.atl_drive_link} onChange={(e) => setForm({ ...form, atl_drive_link: e.target.value })} data-testid="cfd-atl-drive-link" /></F>}
                <F label="Funds with School (INR)" full><Input value={form.atl_school_funds} onChange={(e) => setForm({ ...form, atl_school_funds: e.target.value })} data-testid="cfd-atl-funds" /></F>
              </div>
            </div>
          )}

          <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
            <F label="Assigned to Employee">
              <Select value={form.assigned_to_id} onValueChange={(v) => {
                const p = people.find((x) => x.id === v);
                setForm({ ...form, assigned_to_id: v, assigned_to_name: p?.name || "" });
              }}>
                <SelectTrigger data-testid="cfd-assigned"><SelectValue placeholder="Pick employee…" /></SelectTrigger>
                <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.employee_code}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>

          <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
            <div className="flex items-center gap-3 px-3 py-2 bg-rose-50/60 border border-rose-200 rounded-md">
              <Switch
                checked={!!form.is_hot}
                onCheckedChange={(v) => setForm({
                  ...form,
                  is_hot: v,
                  hot_reason: v ? (form.hot_reason || "Marked Hot on creation — school has interest / funds") : "",
                })}
                data-testid="cfd-is-hot"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-rose-800 flex items-center gap-1.5">
                  🔥 Mark as Hot Client
                </div>
                <div className="text-[11px] text-slate-600">
                  Hot clients show on the Super Hot Clients tab and notify Sales Admin / CRM Manager / Master Admin.
                </div>
              </div>
            </div>
            {form.is_hot && (
              <div className="mt-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Hot Reason</Label>
                <Textarea
                  rows={2}
                  value={form.hot_reason}
                  onChange={(e) => setForm({ ...form, hot_reason: e.target.value })}
                  placeholder="Why is this school hot? (eg. school has funds, ATL signed, kit confirmed)"
                  className="mt-1.5"
                  data-testid="cfd-hot-reason"
                />
              </div>
            )}
          </div>

          <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
            {(() => {
              const isATL = (form.client_types || []).includes("ATL 1.0") || (form.client_types || []).includes("ATL 2.0");
              return (
                <div className={`flex items-center gap-3 px-3 py-2 rounded-md border ${isATL ? "bg-violet-50/60 border-violet-200" : "bg-slate-50 border-slate-200 opacity-70"}`}>
                  <Switch
                    checked={!!form.documentation_required && isATL}
                    disabled={!isATL}
                    onCheckedChange={(v) => setForm({
                      ...form,
                      documentation_required: v,
                      // assignee lists cleared — Master / Doc Admin will assign from the Unassigned queue
                      documentation_assignee_ids: [],
                      documentation_assignee_names: [],
                    })}
                    data-testid="cfd-doc-required"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Documentation Support required?</div>
                    <div className="text-[11px] text-slate-600">
                      {isATL
                        ? "Turn ON to send this client to the Documentation queue. Master / Documentation Admin will assign team members from the Unassigned tab."
                        : "Available only when Client Type is ATL 1.0 or ATL 2.0. Select one of those types to enable."}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        <DialogFooter className="px-6 pb-5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-gradient-to-br from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white shadow-sm" data-testid="cfd-submit">{submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
      {/* v109.9.7 — Save-and-request dialog (auto-opens on 403) */}
      {editReqDialog}
    </Dialog>
  );
}

function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
